/* Anything Wars — fine ground detail: the terrain (and the flat field) look like 0.5 m voxels although the
   simulation grid stays at 2 m cells. Purely visual, all in the fragment shader:
   - every 2 m cell is split into 4×4 sub-voxels; colour per sub-voxel from a small per-cell data texture
     (nearest filtering, see buildTerrainMesh → detail) plus hashed brightness / hue jitter and colour patches,
   - borders between cells of the same surface (path / grass / sand / dry patches) are shifted per sub-voxel by
     blocky noise, so paths and banks get ragged voxel edges instead of 2 m stairs (never across a wall),
   - path edges are darker sub-voxels along that ragged border, paths get pebbles and ruts, banks wet sand,
     terrace rims / bases are darkened per sub-voxel ring instead of per whole cell,
   - cliff walls get finer rock strata (a third of a step) with offset blocks and cracks.
   Only colours change (no depth, no geometry): the screen-space outline pass and shadows are unaffected.
   The detail fades out once a sub-voxel gets smaller than ~1 px (no shimmer in the distance). */

/** Surface kind per vertex (aDet attribute of the terrain mesh). */
export const GD_TOP = 1, GD_WALL = 2;
/** Surface class per cell (low 3 bits of the cell code). */
export const GD_CLS = { grass: 0, path: 1, sand: 2, bed: 3, dry: 4 };
/** Cell code: class | open edges << 3 | terrace rim edges << 7 | terrace base edges << 11 (edge bits: +x, +z, -x, -z). */
export const packCellCode = (cls, open, rim, base) => (cls & 7) | ((open & 15) << 3) | ((rim & 15) << 7) | ((base & 15) << 11);

// shared helpers (terrain + flat field): hash, value noise, detail fade, grass sub-voxel colour
const GD_COMMON = /* glsl */`
float gdH(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float gdN(vec2 p){ vec2 i = floor(p), f = fract(p); f = f*f*(3.0-2.0*f); return mix(mix(gdH(i), gdH(i+vec2(1,0)), f.x), mix(gdH(i+vec2(0,1)), gdH(i+vec2(1,1)), f.x), f.y); }
// 1 while a sub-voxel of size s covers more than ~2 px, 0 once it shrinks to ~1 px
float gdFade(vec3 w, float s){ float fp = length(fwidth(w)); return 1.0 - smoothstep(s * 0.45, s * 1.1, fp); }
// grass / snow sub-voxel: patches (~3 m), clumps (~1 m), per-voxel brightness + hue jitter, dark tufts, light specks
vec3 gdGrass(vec3 col, vec2 sv, vec2 c, float k, float snow){
  float pn = gdN(c * 0.3 + 17.0), pn2 = gdN(c * 0.9 - 5.0), j = gdH(sv), hj = gdH(sv + 31.7), t = gdH(sv * 1.31 + 7.3);
  vec3 tint = snow > 0.0 ? mix(vec3(0.95, 0.97, 1.02), vec3(1.02, 1.02, 1.0), pn) : mix(vec3(0.93, 0.97, 0.9), vec3(1.05, 1.04, 0.95), pn);
  vec3 f = tint * (0.955 + 0.09 * j) * (0.97 + 0.06 * pn2) * vec3(1.0 + (hj - 0.5) * 0.05, 1.0, 1.0 - (hj - 0.5) * 0.05);
  if (t > 0.965) f *= 0.87; else if (t < 0.025) f *= 1.09;
  vec3 r = col * f;
  if (snow > 0.0 && t > 0.5 && t < 0.515) r += vec3(0.12) * snow; // glitter
  return mix(col, r, k);
}
`;

const GD_TERRAIN = /* glsl */`
uniform sampler2D uGD; uniform vec4 uGDGrid; uniform float uGDOn; uniform vec3 uGDPath, uGDPathEdge; uniform float uGDStep, uGDSnow, uGDSeed;
varying float vGDet; varying vec3 vGDW; varying vec3 vGDN;
${GD_COMMON}
ivec2 gdCell(vec2 p){ return ivec2(floor((p - uGDGrid.xy) / uGDGrid.z)); }
vec4 gdAt(ivec2 c){ int n = int(uGDGrid.w + 0.5); return texelFetch(uGD, clamp(c, ivec2(0), ivec2(n - 1)), 0); }
int gdCode(vec4 t){ return int(t.a + 0.5); }
bool gdOpen(int code, int d){ return ((code >> (3 + d)) & 1) == 1; }
// owner of sub-voxel sv (centre c, geometric cell g, its texel tg): the cell border is shifted by blocky noise,
// but only across open edges (same top surface, no wall), so colours never leak over a terrace edge
vec4 gdOwner(vec2 sv, vec2 c, ivec2 g, vec4 tg){
  vec2 off = (vec2(gdN(c * 0.55 + uGDSeed), gdN(c * 0.55 - uGDSeed + 31.0)) - 0.5) * 1.1 + (vec2(gdH(sv + 3.1), gdH(sv - 7.7)) - 0.5) * 0.5;
  ivec2 dd = clamp(gdCell(c + off) - g, ivec2(-1), ivec2(1));
  if (dd == ivec2(0)) return tg;
  int code = gdCode(tg);
  if (dd.x != 0 && !gdOpen(code, dd.x > 0 ? 0 : 2)) return tg;
  if (dd.y != 0 && !gdOpen(code, dd.y > 0 ? 1 : 3)) return tg;
  if (dd.x != 0 && dd.y != 0 && !gdOpen(gdCode(gdAt(g + ivec2(dd.x, 0))), dd.y > 0 ? 1 : 3)) return tg;
  return gdAt(g + dd);
}
vec2 gdDir(int d){ return vec2(d == 0 ? 1.0 : d == 2 ? -1.0 : 0.0, d == 1 ? 1.0 : d == 3 ? -1.0 : 0.0); }
vec3 gdTop(vec3 w, float k){
  k *= 1.0 - 0.45 * uGDSnow; // bright snow shows every percent: calmer there
  float S = uGDGrid.z * 0.25;
  vec2 sv = floor(w.xz / S), c = (sv + 0.5) * S;
  ivec2 g = gdCell(c); vec4 tg = gdAt(g);
  vec4 o = gdOwner(sv, c, g, tg);
  vec3 col = o.rgb; int cls = gdCode(o) & 7, gc = gdCode(tg);
  ivec2 lc = ivec2(floor((c - uGDGrid.xy) / S)) - g * 4; // 0..3 inside the geometric cell
  if (cls == 1) {
    // path: darker sub-voxels along the (ragged) border to anything but path or water
    bool edge = false;
    for (int d = 0; d < 4; d++) {
      vec2 sv2 = sv + gdDir(d), c2 = (sv2 + 0.5) * S; ivec2 g2 = gdCell(c2);
      int cl2 = gdCode(gdOwner(sv2, c2, g2, g2 == g ? tg : gdAt(g2))) & 7;
      if (cl2 != 1 && cl2 != 3) edge = true;
    }
    if (edge) col *= uGDPathEdge / max(uGDPath, vec3(1e-3)) * mix(1.0, 0.96 + 0.08 * gdH(sv + 2.2), k);
    else {
      float pb = gdH(sv * 1.37 + 5.1), rut = abs(gdN(c * 0.3 + uGDSeed * 0.37) - 0.5);
      float f = 0.96 + 0.08 * gdH(sv);
      if (pb > 0.93) f *= gdH(sv + 9.3) > 0.45 ? 0.8 : 1.13; // pebbles
      else if (rut < 0.035) f *= 0.89;                       // ruts / trampled tracks
      col = mix(col, col * f, k);
    }
  } else if (cls == 2 || cls == 3) {
    // sand banks: grains, shells, wet sand along the water; river bed: pebbles
    float f = cls == 2 ? 0.95 + 0.1 * gdH(sv) : 0.92 + 0.16 * gdH(sv);
    float pb = gdH(sv * 1.29 + 3.3);
    if (pb > 0.95) f *= cls == 2 ? 1.1 : 0.78;
    if (cls == 2) {
      float wet = 1.0, r2 = gdH(sv + 41.0);
      for (int d = 0; d < 4; d++) {
        ivec2 g2 = gdCell(c + gdDir(d) * S), g3 = gdCell(c + gdDir(d) * S * 2.0);
        if (g2 != g && (gdCode(gdAt(g2)) & 7) == 3) wet = min(wet, 0.8);
        else if (r2 > 0.45 && g3 != g && (gdCode(gdAt(g3)) & 7) == 3) wet = min(wet, 0.88);
      }
      col *= wet;
    }
    col = mix(col, col * f, k);
  } else col = gdGrass(col, sv, c, k, uGDSnow);
  // terrace rims (lower neighbour) and bases (higher neighbour): outer sub-voxel ring, ragged second ring
  int rim = (gc >> 7) & 15, base = (gc >> 11) & 15;
  if ((rim | base) != 0) {
    bool r2 = gdH(sv + 17.3) > 0.6;
    float f = 1.0;
    for (int d = 0; d < 4; d++) {
      int ed = d == 0 ? 3 - lc.x : d == 1 ? 3 - lc.y : d == 2 ? lc.x : lc.y;
      if (!(ed == 0 || (ed == 1 && r2))) continue;
      if (((rim >> d) & 1) == 1) f = min(f, ed == 0 ? 0.86 : 0.92);
      if (((base >> d) & 1) == 1) f = min(f, ed == 0 ? 0.84 : 0.9);
    }
    col *= f;
  }
  return col;
}
// cliff walls: rock strata a third of a step high, layer borders offset in blocks, per-block jitter and cracks
vec3 gdWall(vec3 col, vec3 w, vec3 n, float k){
  float S = uGDGrid.z * 0.25, L = uGDStep / 3.0;
  float s = abs(n.x) > abs(n.z) ? w.z : w.x, ci = floor(s / S);
  float sh = floor(gdN(vec2(ci * 0.31, 4.7 + uGDSeed)) * 3.0) * L * 0.34;
  float li = floor((w.y + sh) / L);
  float band = 0.88 + 0.2 * gdH(vec2(li, 11.3 + uGDSeed));
  float j = 0.95 + 0.1 * gdH(vec2(ci, li) + n.xz * 13.1);
  float cr = gdH(vec2(ci * 1.7, li * 2.3) + n.xz * 5.7 + 2.9);
  if (cr > 0.955) j *= 0.8; else if (cr < 0.03) j *= 1.1;
  return mix(col, col * band * j, k);
}
`;

// on/off switch shared by every material that uses the detail (one game, one setting)
const GD_ON = { value: 1 };

/** onBeforeCompile hook for plain vertex-coloured voxel meshes on the ground (crater overlays): per 0.5 m
    sub-voxel brightness jitter on a world grid (sampled half a sub-voxel inside the face), like the terrain. */
export function injectVoxelJitter(sh) {
  sh.uniforms.uGDOn = GD_ON;
  sh.vertexShader = sh.vertexShader.replace('void main() {', 'varying vec3 vGDW; varying vec3 vGDN;\nvoid main() {')
    .replace('#include <project_vertex>', '#include <project_vertex>\nvGDW = (modelMatrix * vec4(transformed, 1.0)).xyz; vGDN = mat3(modelMatrix) * objectNormal;');
  sh.fragmentShader = sh.fragmentShader.replace('void main() {', 'uniform float uGDOn; varying vec3 vGDW; varying vec3 vGDN;\n' + GD_COMMON + '\nvoid main() {')
    .replace('#include <color_fragment>', `#include <color_fragment>
  float gdK = gdFade(vGDW, 0.5) * uGDOn;
  vec3 gdG = floor(vGDW * 2.0 - normalize(vGDN) * 0.5);
  float gdJ = gdH(gdG.xz + gdG.y * 17.31), gdT = gdH(gdG.zx * 1.31 + gdG.y * 3.7 + 7.3);
  diffuseColor.rgb *= mix(1.0, (0.93 + 0.14 * gdJ) * (gdT > 0.95 ? 0.82 : gdT < 0.03 ? 1.12 : 1.0), gdK);`);
}

/**
 * Detail state for the terrain material and the flat ground material.
 * uniforms.uGDOn: 1 = on, 0 = the plain vertex colours (cheaper; "Automatisch" step).
 */
export function createGroundDetail(THREE, { snow } = {}) {
  const blank = new THREE.DataTexture(new Float32Array(4), 1, 1, THREE.RGBAFormat, THREE.FloatType);
  blank.needsUpdate = true;
  const U = {
    uGD: { value: blank }, uGDGrid: { value: new THREE.Vector4(0, 0, 2, 1) }, uGDOn: GD_ON,
    uGDPath: { value: new THREE.Color(1, 1, 1) }, uGDPathEdge: { value: new THREE.Color(1, 1, 1) },
    uGDStep: { value: 1.2 }, uGDSnow: snow || { value: 0 }, uGDSeed: { value: 0 },
  };
  let tex = null;
  return {
    uniforms: U,
    /** use the per-cell data of a freshly built terrain mesh (buildTerrainMesh(...).detail); null = none */
    bind(d) {
      if (tex) { tex.dispose(); tex = null; }
      if (!d) { U.uGD.value = blank; return; }
      tex = new THREE.DataTexture(d.data, d.n, d.n, THREE.RGBAFormat, THREE.FloatType);
      tex.magFilter = tex.minFilter = THREE.NearestFilter; tex.generateMipmaps = false; tex.flipY = false; tex.needsUpdate = true;
      U.uGD.value = tex; U.uGDGrid.value.set(d.origin, d.origin, d.cell, d.n);
      U.uGDPath.value.setRGB(d.path[0], d.path[1], d.path[2]); U.uGDPathEdge.value.setRGB(d.pathEdge[0], d.pathEdge[1], d.pathEdge[2]);
      U.uGDStep.value = d.stepHeight; U.uGDSeed.value = ((d.seed >>> 0) % 997) * 0.173;
    },
    set enabled(on) { U.uGDOn.value = on ? 1 : 0; },
    get enabled() { return U.uGDOn.value > 0.5; },
    /** onBeforeCompile hook for the terrain material (vertex colours + aDet attribute) */
    injectTerrain(sh) {
      Object.assign(sh.uniforms, U);
      sh.vertexShader = sh.vertexShader.replace('void main() {', 'attribute float aDet; varying float vGDet; varying vec3 vGDW; varying vec3 vGDN;\nvoid main() {')
        .replace('#include <project_vertex>', '#include <project_vertex>\nvGDet = aDet; vGDW = (modelMatrix * vec4(transformed, 1.0)).xyz; vGDN = mat3(modelMatrix) * objectNormal;');
      sh.fragmentShader = sh.fragmentShader.replace('void main() {', GD_TERRAIN + '\nvoid main() {')
        .replace('#include <color_fragment>', `#include <color_fragment>
  float gdK = gdFade(vGDW, uGDGrid.z * 0.25);
  if (uGDOn > 0.5 && vGDet > 0.5) diffuseColor.rgb = vGDet < 1.5 ? gdTop(vGDW, gdK) : gdWall(diffuseColor.rgb, vGDW, normalize(vGDN), gdK);`);
    },
    /** onBeforeCompile hook for the flat ground plane (main.js groundMat): blocky 0.5 m colour fields + grass detail.
        Expects its procedural colour code (vec2 q = vW.xz; … the tile line); does nothing if that changed. */
    injectFlat(sh, snowUniform = 'uSnow') {
      const f = sh.fragmentShader, A = 'vec2 q = vW.xz;', B = 'g *= 0.93 + tile * 0.07 + (n3 - 0.5) * 0.08;';
      if (!f.includes(A) || !f.includes(B)) return;
      sh.uniforms.uGDOn = U.uGDOn;
      sh.fragmentShader = f.replace('void main() {', 'uniform float uGDOn;\n' + GD_COMMON + '\nvoid main() {')
        .replace(A, `float gdK = gdFade(vW, 0.5) * uGDOn * (1.0 - 0.45 * ${snowUniform}); vec2 gdSv = floor(vW.xz * 2.0);
  vec2 q = mix(vW.xz, (gdSv + 0.5) * 0.5, step(0.5, uGDOn));`)
        .replace(B, B + `\n  g = gdGrass(g, gdSv, q, gdK, ${snowUniform});`);
    },
    dispose() { if (tex) tex.dispose(); tex = null; U.uGD.value = blank; },
  };
}
